import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateLong } from '../utils/dateUtils';
import { 
  Box, 
  Typography, 
  Container,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Paper
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import StatusBadge from '../components/orders/StatusBadge';
import WorkflowActions from '../components/orders/WorkflowActions';
import WorkflowHistory from '../components/orders/WorkflowHistory';
import useAdOrders from '../hooks/useAdOrders';
import useOrderSync from '../hooks/useOrderSync';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';
import StationeryOrderForm from '../components/StationeryOrderForm';
import StationeryTabs from '../components/stationery/StationeryTabs';
import { uploadTempAttachments } from '../utils/attachmentUpload';
import EnquiryDetailDialog from '../components/stationery/EnquiryDetailDialog';
import OrganizationDialog from '../components/organizations/OrganizationDialog';
import EnquiryReferencePanel from '../components/stationery/EnquiryReferencePanel';
import { createStructuredNote, parseNotesBlob } from '../utils/noteUtils';
import NoteCard from '../components/notes/NoteCard';
import { 
  NoteAdd as NoteAddIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Helper function to parse notes into individual note objects. Delegates the
// bulk of the parsing to the shared parseNotesBlob() (which understands both
// the current structured "(date) User from Source: content" format and the
// legacy "[timestamp] content" format used previously in this file) so
// stationery notes are consistent with every other notes section in the app,
// while preserving the stationery-specific client-revision and
// customer/system authorship detection.
const parseNotesIntoArray = (notesString) => {
  if (!notesString || !notesString.trim()) return [];

  const parsedNotes = [];

  // First, check for CLIENT REVISION REQUEST format (proof revisions)
  const revisionSeparator = '🔄 === CLIENT REVISION REQUEST ===';
  if (notesString.includes(revisionSeparator)) {
    const parts = notesString.split(revisionSeparator);

    // First part is regular notes (before revisions)
    if (parts[0].trim()) {
      parsedNotes.push(...parseRegularNotes(parts[0]));
    }

    // Parse each revision request
    for (let i = 1; i < parts.length; i++) {
      const revisionText = parts[i];
      const dateMatch = revisionText.match(/Date: ([^\n]+)/);
      const feedbackMatch = revisionText.match(/Client Feedback: ([^\n]+)/);

      if (feedbackMatch) {
        parsedNotes.push({
          id: `revision_${i}_${Date.now()}`,
          content: feedbackMatch[1].trim(),
          timestamp: dateMatch ? dateMatch[1] : null,
          author: 'Customer'
        });
      }
    }
  } else {
    // Parse as regular notes
    parsedNotes.push(...parseRegularNotes(notesString));
  }

  return parsedNotes;
};

// Helper to parse regular (non-revision) notes via the shared parseNotesBlob(),
// then adapt its output to the {id, content, timestamp, author} shape this
// file's call sites expect, applying the same client/system detection rules
// used previously so existing behaviour is preserved.
const parseRegularNotes = (notesString) => {
  if (!notesString || !notesString.trim()) return [];

  // Default author "Staff" matches this file's previous behaviour for notes
  // that don't carry a recognisable user name (legacy bracket/plain notes).
  const parsed = parseNotesBlob(notesString, { idPrefix: 'note', noteType: 'general', createdBy: 'Staff' });

  return parsed.map((note) => {
    let author = note.createdBy || 'Staff';
    let content = note.content;

    if (content.includes('Customer Change Request:') || content.includes('CLIENT CHANGE REQUEST:')) {
      author = 'Customer';
      content = content.replace(/Customer Change Request:|CLIENT CHANGE REQUEST:/gi, '').trim();
    } else if (content.toLowerCase().includes('client action:') ||
               content.toLowerCase().includes('client requested') ||
               content.toLowerCase().includes('client accepted') ||
               content.toLowerCase().includes('client approved')) {
      author = 'Customer';
      content = content.replace(/Client Action:\s*/gi, '').trim();
    } else if (content.toLowerCase().includes('converted from enquiry') ||
               content.toLowerCase().includes('status automatically changed')) {
      author = 'System';
    }

    return {
      id: note.id,
      content,
      timestamp: note.createdAt,
      author,
      sourceLabel: note.sourceLabel,
      raw: note.raw
    };
  });
};

// Helper function to parse client revision requests from design notes (Order Workflow version)
const parseClientRevisionRequests = (designNotes) => {
  if (!designNotes) return [];
  
  const revisions = [];
  const lines = designNotes.split('\n');
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Look for patterns like "Client Revision Request:" or "Client requested:"
    if (trimmedLine.toLowerCase().includes('client revision') || 
        trimmedLine.toLowerCase().includes('client requested')) {
      
      // Extract the request text (everything after the colon or the whole line)
      let requestText = trimmedLine;
      if (trimmedLine.includes(':')) {
        requestText = trimmedLine.split(':').slice(1).join(':').trim();
      }
      
      // If we found a header but no text, check the next line
      if (!requestText && index + 1 < lines.length) {
        requestText = lines[index + 1].trim();
      }
      
      if (requestText) {
        revisions.push({
          id: `revision-${index}`,
          text: requestText,
          timestamp: new Date().toISOString(), // We don't have actual timestamp
          status: 'pending'
        });
      }
    }
  });
  
  return revisions;
};

// Helper function to determine product type from order data
const determineProductType = (order) => {
  // Check if this is a school order
  if (order.orderType === 'school') {
    return 'school';
  }
  
  // Check if this is a stationery order
  if (order.orderType === 'stationery') {
    return 'stationery';
  }
  
  // Check for stationery-specific fields
  if (order.productType || order.product) {
    return 'stationery';
  }
  
  // Check if order has audiences (advertising orders have audiences)
  if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
    return 'advertising';
  }
  
  // Check for school-specific fields
  if (order.schoolName || order.schoolId) {
    return 'school';
  }
  
  // Default to advertising if we can't determine
  return 'advertising';
};

// Invoices are pushed to Xero and the link is stored in xero_invoice_url;
// invoiceLink is only ever populated for orders invoiced before the Xero
// integration existed. Fall back to it so those older orders still show a link.
const getStationeryInvoiceUrl = (order) => order?.xero_invoice_url || order?.invoiceLink || null;

// Hide the cancel action once a Xero invoice has already been voided.
// Legacy (pre-Xero) invoices only ever have invoiceLink and can still be
// "cancelled" locally (there's nothing in Xero to void for those).
const canCancelStationeryInvoice = (order) => {
  if (order?.xero_invoice_id) return order.xero_invoice_status !== 'VOIDED';
  return !!order?.invoiceLink;
};

// Xero-backed invoices are voided via the dedicated endpoint; legacy invoices
// (created before the Xero integration existed) fall back to the old
// link-clearing endpoint since there's no real Xero invoice behind them.
const getStationeryInvoiceCancelUrl = (order, apiUrl) => order?.xero_invoice_id
  ? `${apiUrl}/api/stationery-orders/invoice/cancel/${order.id}`
  : `${apiUrl}/api/proof/cancel-invoice/${order.id}`;

const StationeryOrders = () => {
  const { updateOrderStatus } = useAdOrders();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [stationeryOrders, setStationeryOrders] = useState([]);
  const [stationeryEnquiries, setStationeryEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getToken, user } = useAuth();
  const [newNote, setNewNote] = useState('');
  const [stationerySettings, setStationerySettings] = useState({
    marginPercentage: 25,
    artworkFee: 45,
    setupFee: 25,
    gstPercentage: 10
  });
  const navigate = useNavigate();
  
  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'organization'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch stationery settings
  useEffect(() => {
    const fetchStationerySettings = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${config.apiUrl}/api/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setStationerySettings({
              marginPercentage: parseFloat(data.data.stationeryMarginPercentage) || 25,
              artworkFee: parseFloat(data.data.stationeryArtworkFee) || 45,
              setupFee: parseFloat(data.data.stationerySetupFee) || 25,
              gstPercentage: parseFloat(data.data.stationeryGstPercentage) || 10
            });
          }
        }
      } catch (error) {
        console.error('Error fetching stationery settings:', error);
      }
    };

    fetchStationerySettings();
  }, [getToken]);
  
  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });

  // Create order dialog state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  
  // Enquiry detail dialog state
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [enquiryDetailOpen, setEnquiryDetailOpen] = useState(false);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [enquiryForOrder, setEnquiryForOrder] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  
  // Attachments state
  const [orderAttachments, setOrderAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  
  const [formData, setFormData] = useState({
    organization_id: '',
    business_number: '',
    business_name: '',
    business_type: '',
    orderType: 'stationery',
    productType: '',
    notes: '',
    designNotes: '',
    quantity: '',
    size: '',
    sides: '',
    stock: '',
    finish: '',
    price: '',
    freightPrice: '',
    additionalArtworkHours: '',
    margin: '',
    artworkFee: '',
    setupFee: '',
    gst: '',
    discountType: '$',
    discountAmount: '',
    totalPrice: '',
    tempAttachments: [] // Initialize tempAttachments
  });



  // ProofButtons Component for workflow table - now returns menu items
  const ProofButtons = ({ order, onClose }) => {
    const [cancelling, setCancelling] = useState(false);
    
    const handleViewProof = async () => {
      onClose();
      try {
        const token = await getToken();
        const response = await axios.get(`${config.apiUrl}/api/proofs/order/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.success && response.data.data) {
          const proof = response.data.data;
          if (proof.proofLink) {
            window.open(proof.proofLink, '_blank');
          } else {
            setError('Proof link not available');
          }
        } else {
          setError('Proof not found');
        }
      } catch (error) {
        console.error('Error fetching proof:', error);
        setError('Failed to fetch proof');
      }
    };

    const handleCancelProof = async () => {
      if (!window.confirm('Are you sure you want to cancel this proof? This action cannot be undone.')) {
        return;
      }

      setCancelling(true);
      try {
        const token = await getToken();
        const response = await axios.put(
          `${config.apiUrl}/api/proofs/order/${order.id}/cancel`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data && response.data.success) {
          setSuccessMessage('Proof cancelled successfully');
          await fetchOrders();
          onClose();
        } else {
          setError('Failed to cancel proof');
        }
      } catch (error) {
        console.error('Error cancelling proof:', error);
        setError('Failed to cancel proof');
      } finally {
        setCancelling(false);
      }
    };

    return (
      <>
        <MenuItem onClick={handleViewProof}>
          <ListItemText>View Proof</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCancelProof} disabled={cancelling}>
          <ListItemText sx={{ color: 'error.main' }}>
            {cancelling ? 'Cancelling...' : 'Cancel Proof'}
          </ListItemText>
        </MenuItem>
      </>
    );
  };

  // Actions Menu Component - Consolidates all actions into a dropdown menu
  const ActionsMenu = ({ order }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const isStationeryOrder = order.orderType === 'stationery';
    const [menuPosition, setMenuPosition] = useState({ vertical: 'bottom', horizontal: 'right' });

    const handleClick = (event) => {
      const button = event.currentTarget;
      const buttonRect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Check if there's enough space below (need ~400px for full menu)
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // If not enough space below but more space above, open upward
      if (spaceBelow < 400 && spaceAbove > spaceBelow) {
        setMenuPosition({ vertical: 'top', horizontal: 'right' });
      } else {
        setMenuPosition({ vertical: 'bottom', horizontal: 'right' });
      }
      
      setAnchorEl(button);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleViewDetails = async () => {
      handleClose();
      // Open the order details dialog instead of navigating
      setSelectedOrder(order);
      // Create editable copy with calculated values
      const calculated = calculateValues(order);
      setEditedOrder({ 
        ...order,
        margin: calculated.margin,
        artworkFee: calculated.artworkFee,
        setupFee: calculated.setupFee,
        gst: calculated.gst,
        totalPrice: calculated.totalPrice
      });
      setDetailDialogOpen(true);
      
      // Fetch attachments for this order
      await fetchAttachments(order.id);
    };

    const handleOpenNotes = () => {
      handleClose();
      const existingNotes = [];
      
      // Parse order.notes into individual notes
      if (order.notes) {
        const parsedNotes = parseNotesIntoArray(order.notes);
        parsedNotes.forEach((note, idx) => {
          // Map author names consistently
          let displayAuthor = note.author;
          let noteType = 'sales';
          
          if (note.author === 'Customer') {
            displayAuthor = 'Customer';
            noteType = 'customer';
          } else if (note.author === 'System') {
            displayAuthor = 'System';
            noteType = 'sales';
          } else {
            displayAuthor = 'Staff';
            noteType = 'sales';
          }
          
          existingNotes.push({
            id: `note_${idx}`,
            content: note.content,
            createdBy: displayAuthor,
            createdAt: note.timestamp || order.createdAt || new Date().toISOString(),
            isNew: false,
            noteType: noteType
          });
        });
      }
      
      // Parse design notes similarly
      if (order.designNotes) {
        const parsedDesignNotes = parseNotesIntoArray(order.designNotes);
        parsedDesignNotes.forEach((note, idx) => {
          existingNotes.push({
            id: `design_${idx}`,
            content: note.content,
            createdBy: note.author === 'Customer' ? 'Customer' : 'Design Team',
            createdAt: note.timestamp || order.createdAt || new Date().toISOString(),
            isNew: false,
            noteType: 'design'
          });
        });
      }
      
      handleOpenNotesDialog('order', order.id, `Order #${order.id} - ${order.organizationName || order.businessName || order.customerName || 'Unknown'}`, existingNotes);
    };

    const totalNoteCount = getTotalNoteCount('order', order);

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ 
            color: 'primary.main',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: menuPosition.vertical,
            horizontal: menuPosition.horizontal,
          }}
          transformOrigin={{
            vertical: menuPosition.vertical === 'top' ? 'bottom' : 'top',
            horizontal: menuPosition.horizontal,
          }}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: '250px',
            }
          }}
        >
          {/* View Details */}
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>

          {/* Notes */}
          <MenuItem onClick={handleOpenNotes}>
            <ListItemIcon>
              <NoteAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Notes {totalNoteCount > 0 && `(${totalNoteCount})`}
            </ListItemText>
          </MenuItem>

          <Divider />

          {/* Quote Actions - Only show if quote is active */}
          {order.status?.toLowerCase() === 'quote active' && order.quoteLink && (
            <>
              <MenuItem onClick={() => {
                handleClose();
                window.open(order.quoteLink, '_blank');
              }}>
                <ListItemText>View Quote</ListItemText>
              </MenuItem>
              <MenuItem onClick={async () => {
                handleClose();
                if (!window.confirm('Are you sure you want to cancel this quote? This will deactivate the quote link and set the status back to Quote.')) {
                  return;
                }
                try {
                  const token = await getToken();
                  const response = await axios.post(
                    `${config.apiUrl}/api/quote/cancel-admin/${order.id}`,
                    { reason: 'Quote cancelled by admin' },
                    {
                      headers: { 'Authorization': `Bearer ${token}` }
                    }
                  );
                  if (response.data && response.data.success) {
                    setSuccessMessage('Quote cancelled successfully. Status has been reverted to "Quote".');
                    await fetchOrders();
                  } else {
                    setError('Failed to cancel quote');
                  }
                } catch (error) {
                  console.error('Error cancelling quote:', error);
                  setError('Failed to cancel quote: ' + (error.response?.data?.message || error.message));
                }
              }}>
                <ListItemText sx={{ color: 'error.main' }}>Cancel Quote</ListItemText>
              </MenuItem>
            </>
          )}

          {/* Proof Actions - Only show if proof is active */}
          {order.status?.toLowerCase() === 'proof active' && order.proofLink && (
            <>
              <MenuItem onClick={() => {
                handleClose();
                window.open(order.proofLink, '_blank');
              }}>
                <ListItemText>View Proof</ListItemText>
              </MenuItem>
              <MenuItem onClick={async () => {
                handleClose();
                if (!window.confirm('Are you sure you want to cancel this proof? This will deactivate the proof link and set the status back to Proof.')) {
                  return;
                }
                try {
                  const token = await getToken();
                  const response = await axios.post(
                    `${config.apiUrl}/api/proof/cancel/${order.id}`,
                    { reason: 'Proof cancelled by admin' },
                    {
                      headers: { 'Authorization': `Bearer ${token}` }
                    }
                  );
                  if (response.data && response.data.success) {
                    setSuccessMessage('Proof cancelled successfully. Status has been reverted to "Proof".');
                    await fetchOrders();
                  } else {
                    setError('Failed to cancel proof');
                  }
                } catch (error) {
                  console.error('Error cancelling proof:', error);
                  setError('Failed to cancel proof: ' + (error.response?.data?.message || error.message));
                }
              }}>
                <ListItemText sx={{ color: 'error.main' }}>Cancel Proof</ListItemText>
              </MenuItem>
            </>
          )}

          {/* View / Cancel Invoice - shown whenever this order has an invoice (Xero or legacy) */}
          {getStationeryInvoiceUrl(order) && (
            <>
              <MenuItem onClick={() => {
                handleClose();
                window.open(getStationeryInvoiceUrl(order), '_blank');
              }}>
                <ListItemText>View Invoice</ListItemText>
              </MenuItem>
              {canCancelStationeryInvoice(order) && (
                <MenuItem onClick={async () => {
                  handleClose();
                  if (!window.confirm('Are you sure you want to cancel this invoice? This will void it in Xero and set the status back to In Production.')) return;
                  try {
                    const token = await getToken();
                    const response = await axios.post(
                      getStationeryInvoiceCancelUrl(order, config.apiUrl),
                      { reason: 'Invoice cancelled by admin' },
                      { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (response.data?.success) {
                      setSuccessMessage(response.data.message || 'Invoice cancelled successfully. Status has been reverted to "In Production".');
                      await fetchOrders();
                    } else {
                      setError(response.data?.message || 'Failed to cancel invoice');
                    }
                  } catch (error) {
                    console.error('Error cancelling invoice:', error);
                    setError('Failed to cancel invoice: ' + (error.response?.data?.message || error.message));
                  }
                }}>
                  <ListItemText sx={{ color: 'error.main' }}>Cancel Invoice</ListItemText>
                </MenuItem>
              )}
            </>
          )}

          {/* View Changes - Only show if status is Quote Needs Changes or Proof Needs Changes */}
          {(order.status?.toLowerCase() === 'quote needs changes' || 
            order.status?.toLowerCase() === 'changes needed' ||
            order.status?.toLowerCase() === 'proof needs changes') && (
            <>
              <MenuItem onClick={async () => {
                handleClose();
                try {
                  // First, try to get change requests from the order notes field
                  const token = await getToken();
                  const orderResponse = await axios.get(
                    `${config.apiUrl}/api/stationery-orders/${order.id}`,
                    {
                      headers: { 'Authorization': `Bearer ${token}` }
                    }
                  );
                  
                  if (orderResponse.data && orderResponse.data.success && orderResponse.data.data) {
                    const orderData = orderResponse.data.data;
                    
                    // Parse notes field for change requests
                    if (orderData.notes) {
                      const notesLines = orderData.notes.split('\n');
                      const changeRequests = [];
                      let currentRequest = null;
                      
                      notesLines.forEach(line => {
                        // Look for change request headers (both old and new formats)
                        const timestampMatch = line.match(/^\[(.*?)\]\s*(?:Customer Change Request|CLIENT CHANGE REQUEST):/i);
                        if (timestampMatch) {
                          if (currentRequest) {
                            changeRequests.push(currentRequest);
                          }
                          currentRequest = {
                            timestamp: timestampMatch[1],
                            text: ''
                          };
                        } else if (currentRequest && line.trim()) {
                          currentRequest.text += (currentRequest.text ? '\n' : '') + line;
                        }
                      });
                      
                      if (currentRequest) {
                        changeRequests.push(currentRequest);
                      }
                      
                      if (changeRequests.length > 0) {
                        const changesList = changeRequests.map((request, index) => {
                          return `${index + 1}. ${request.text}\n   Date: ${request.timestamp}`;
                        }).join('\n\n');
                        
                        alert(`Client Change Requests:\n\n${changesList}`);
                        return;
                      }
                    }
                  }
                  
                  // Fallback: try the order_notes API
                  const response = await axios.get(
                    `${config.apiUrl}/api/quote/order-notes/${order.id}`,
                    {
                      headers: { 'Authorization': `Bearer ${token}` }
                    }
                  );
                  
                  if (response.data && response.data.success && response.data.data) {
                    const clientNotes = response.data.data.filter(note => 
                      note.noteType === 'client_action' && note.note?.includes('Request Changes')
                    );
                    
                    if (clientNotes.length > 0) {
                      const changesList = clientNotes.map((note, index) => {
                        const changeRequest = note.note.replace('Client Action: Request Changes - ', '');
                        const date = new Date(note.createdAt).toLocaleString();
                        return `${index + 1}. ${changeRequest}\n   Date: ${date}`;
                      }).join('\n\n');
                      
                      alert(`Client Change Requests:\n\n${changesList}`);
                      return;
                    }
                  }
                  
                  alert('No change requests found for this order.');
                } catch (error) {
                  console.error('Error fetching change requests:', error);
                  setError('Failed to fetch change requests: ' + (error.response?.data?.message || error.message));
                }
              }}>
                <ListItemText>View Changes</ListItemText>
              </MenuItem>
            </>
          )}

          {/* Proof Actions - Only show if proof exists */}
          {order.proofStatus && order.proofStatus !== 'none' && (
            <ProofButtons order={order} onClose={handleClose} />
          )}

          <Divider />

          {/* Status Actions Section */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="medium">
              Status Actions
            </Typography>
          </Box>

          {/* Workflow Status Change Actions */}
          <Box sx={{ p: 2 }}>
            <WorkflowActions
              order={order}
              onStatusChange={handleStatusChange}
              onCreateProof={handleCreateProof}
              onCreateQuote={handleCreateQuote}
              renderAsMenuItems={false}
            />
          </Box>
        </Menu>
      </>
    );
  };

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API returns { success: true, data: [...], pagination: {...} }
      // We need to extract the data array
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setOrganizations(response.data.data);
      } else if (Array.isArray(response.data)) {
        // Fallback for legacy format
        setOrganizations(response.data);
      } else {
        console.warn('Unexpected organizations response format:', response.data);
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    }
  }, [getToken]);

  // Fetch enquiries (excluding converted ones)
  const fetchEnquiries = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/stationery-enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Filter out converted enquiries - they should only appear in Archive
        const activeEnquiries = response.data.data.filter(enquiry => enquiry.status !== 'converted');
        setStationeryEnquiries(activeEnquiries);
      } else {
        console.warn('Unexpected enquiries response format');
        setStationeryEnquiries([]);
      }
    } catch (error) {
      console.error('Error fetching stationery enquiries:', error);
      setStationeryEnquiries([]);
    }
  }, [getToken]);

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();

      // Fetch stationery orders
      try {
        const stationeryResponse = await axios.get(`${config.apiUrl}/api/stationery-orders`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (stationeryResponse.data && stationeryResponse.data.success && Array.isArray(stationeryResponse.data.data)) {
          setStationeryOrders(stationeryResponse.data.data);
        } else {
          console.warn('Unexpected stationery orders response format');
          setStationeryOrders([]);
        }
      } catch (stationeryError) {
        console.warn('Error fetching stationery orders:', stationeryError);
        setStationeryOrders([]);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Initial fetch
  useEffect(() => {
    fetchOrders();
    fetchOrganizations();
    fetchEnquiries();
  }, [fetchOrders, fetchOrganizations, fetchEnquiries]);

  // Set up real-time sync for orders
  useOrderSync({
    onOrderUpdate: (updatedOrder) => {
      
      const orderType = determineProductType(updatedOrder);
      
      if (orderType === 'stationery') {
        setStationeryOrders(prevOrders => {
          const existingIndex = prevOrders.findIndex(o => o.id === updatedOrder.id);
          if (existingIndex >= 0) {
            const newOrders = [...prevOrders];
            newOrders[existingIndex] = updatedOrder;
            return newOrders;
          } else {
            return [updatedOrder, ...prevOrders];
          }
        });
      }
    },
    onOrderCreate: (newOrder) => {
      
      const orderType = determineProductType(newOrder);
      
      if (orderType === 'stationery') {
        setStationeryOrders(prevOrders => {
          const exists = prevOrders.some(o => o.id === newOrder.id);
          if (!exists) {
            return [newOrder, ...prevOrders];
          }
          return prevOrders;
        });
      }
    }
  });

  // Handle notes dialog
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      notes: existingNotes
    });
  };

  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
  };

  const handleSaveNotes = async (notes) => {
    try {
      const token = await getToken();
      
      // Separate notes by type
      const generalNotes = notes.filter(note => note.noteType === 'general');
      const designNotes = notes.filter(note => note.noteType === 'design');
      
      // Combine notes into strings
      const generalNotesText = generalNotes.map(note => note.content).join('\n\n');
      const designNotesText = designNotes.map(note => note.content).join('\n\n');
      
      // Update the order with new notes
      const updateData = {
        notes: generalNotesText || null,
        designNotes: designNotesText || null
      };
      
      const response = await axios.put(
        `${config.apiUrl}/api/${notesDialog.entityType}s/${notesDialog.entityId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.success) {
        setSuccessMessage('Notes saved successfully');
        await fetchOrders();
        handleCloseNotesDialog();
      } else {
        setError('Failed to save notes');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      setError('Failed to save notes');
    }
  };

  // Get total note count for an entity
  const getTotalNoteCount = (entityType, entity) => {
    let count = 0;
    if (entity.notes) count++;
    if (entity.designNotes) count++;
    return count;
  };

  // Filter orders based on search query and status filter
  const filterOrders = (orders) => {
    return orders.filter(order => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        order.id?.toString().includes(searchLower) ||
        order.organizationName?.toLowerCase().includes(searchLower) ||
        order.businessName?.toLowerCase().includes(searchLower) ||
        order.customerName?.toLowerCase().includes(searchLower);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        order.status?.toLowerCase() === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  // Sort orders
  const sortOrders = (orders) => {
    const sorted = [...orders];
    
    sorted.sort((a, b) => {
      let compareValue = 0;
      
      if (sortBy === 'date') {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        compareValue = dateA - dateB;
      } else if (sortBy === 'organization') {
        const nameA = (a.organizationName || a.businessName || '').toLowerCase();
        const nameB = (b.organizationName || b.businessName || '').toLowerCase();
        compareValue = nameA.localeCompare(nameB);
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
    
    return sorted;
  };

  // Get filtered and sorted orders
  const getProcessedOrders = (orders) => {
    return sortOrders(filterOrders(orders));
  };

  // Get unique statuses for filter dropdown
  const getUniqueStatuses = (orders) => {
    const statuses = new Set();
    orders.forEach(order => {
      if (order.status) {
        statuses.add(order.status);
      }
    });
    return Array.from(statuses).sort();
  };

  // Calculate total price
  const getTotalPrice = (order) => {
    // Fall back to direct value field or totalPrice
    return parseFloat(order.totalPrice || order.value || order.price || 0).toFixed(2);
  };

  const handleViewDetails = async (orderId) => {
    const order = stationeryOrders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      // Create editable copy with calculated values
      const calculated = calculateValues(order);
      setEditedOrder({ 
        ...order,
        margin: calculated.margin,
        artworkFee: calculated.artworkFee,
        setupFee: calculated.setupFee,
        gst: calculated.gst,
        totalPrice: calculated.totalPrice
      });
      setDetailDialogOpen(true);
      
      // Fetch attachments for this order
      await fetchAttachments(orderId);
    }
  };
  
  // Fetch attachments for an order
  const fetchAttachments = async (orderId) => {
    if (!orderId) {
      return;
    }
    
    try {
      setLoadingAttachments(true);
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/order-attachments/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        setOrderAttachments(response.data.data || []);
      } else {
        setOrderAttachments([]);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setOrderAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };
  
  // Download an attachment
  const handleDownloadAttachment = async (attachmentId, originalName) => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${config.apiUrl}/api/order-attachments/download/${attachmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };
  
  // Delete an attachment
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }
    
    try {
      const token = await getToken();
      await axios.delete(`${config.apiUrl}/api/order-attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh attachments list
      if (selectedOrder) {
        await fetchAttachments(selectedOrder.id);
      }
      
      setSuccessMessage('Attachment deleted successfully');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setError('Failed to delete attachment');
    }
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setEditedOrder(null);
    setNewNote('');
    setOrderAttachments([]);
  };

  // Calculate derived values
  const calculateValues = (updatedOrder) => {
    const price = parseFloat(updatedOrder.price) || 0;
    const freightPrice = parseFloat(updatedOrder.freightPrice) || 0;
    const additionalArtworkHours = parseFloat(updatedOrder.additionalArtworkHours) || 0;
    
    // Calculate margin (percentage of price)
    const margin = price * (stationerySettings.marginPercentage / 100);
    
    // Calculate artwork fee (additional hours * rate + base fee)
    const artworkFee = stationerySettings.artworkFee + (additionalArtworkHours * stationerySettings.artworkFee);
    
    // Setup fee is fixed
    const setupFee = stationerySettings.setupFee;
    
    // Calculate subtotal before GST and discount
    const subtotalBeforeGst = price + freightPrice + margin + artworkFee + setupFee;
    
    // Calculate GST (percentage of subtotal)
    const gst = subtotalBeforeGst * (stationerySettings.gstPercentage / 100);
    
    // Calculate discount
    const discountAmount = parseFloat(updatedOrder.discountAmount) || 0;
    const discountType = updatedOrder.discountType || '$';
    let discountValue = 0;
    
    if (discountAmount > 0) {
      if (discountType === '%') {
        discountValue = (subtotalBeforeGst + gst) * (discountAmount / 100);
      } else {
        discountValue = discountAmount;
      }
    }
    
    // Calculate total
    const total = subtotalBeforeGst + gst - discountValue;
    
    return {
      margin: margin.toFixed(2),
      artworkFee: artworkFee.toFixed(2),
      setupFee: setupFee.toFixed(2),
      gst: gst.toFixed(2),
      totalPrice: total.toFixed(2)
    };
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    const updatedOrder = { ...editedOrder, [field]: value };
    
    // Recalculate if price-related fields changed
    if (['price', 'freightPrice', 'additionalArtworkHours', 'discountAmount', 'discountType'].includes(field)) {
      const calculated = calculateValues(updatedOrder);
      setEditedOrder({
        ...updatedOrder,
        margin: calculated.margin,
        artworkFee: calculated.artworkFee,
        setupFee: calculated.setupFee,
        gst: calculated.gst,
        totalPrice: calculated.totalPrice
      });
    } else {
      setEditedOrder(updatedOrder);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const token = await getToken();

      // Use the same "(date) User from Source: content" structured format used
      // everywhere else so this note parses and displays consistently via the
      // shared parseNotesBlob()/NoteCard system.
      const userName = user?.name || user?.email || 'Staff';
      const formattedNote = createStructuredNote(newNote.trim(), userName, 'stationeryOrder', 'general');

      // Append to existing notes
      const updatedNotes = editedOrder.notes 
        ? `${editedOrder.notes}\n\n${formattedNote}`
        : formattedNote;

      const response = await axios.put(
        `${config.apiUrl}/api/stationery-orders/${editedOrder.id}`,
        {
          notes: updatedNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNewNote('');
        // Update local state
        const updatedOrder = { ...editedOrder, notes: updatedNotes };
        setEditedOrder(updatedOrder);
        setSelectedOrder(updatedOrder);
        setSuccessMessage('Note added successfully');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setError('Failed to add note');
    }
  };

  const handleSaveOrderDetails = async () => {
    try {
      const token = await getToken();
      const payload = {
        productType: editedOrder.productType || '',
        quantity: parseInt(editedOrder.quantity) || 0,
        size: editedOrder.size || '',
        sides: editedOrder.sides || '',
        stock: editedOrder.stock || '',
        finish: editedOrder.finish || '',
        price: parseFloat(editedOrder.price) || 0,
        freightPrice: parseFloat(editedOrder.freightPrice) || 0,
        additionalArtworkHours: parseFloat(editedOrder.additionalArtworkHours) || 0,
        artworkFee: parseFloat(editedOrder.artworkFee) || 0,
        margin: parseFloat(editedOrder.margin) || 0,
        setupFee: parseFloat(editedOrder.setupFee) || 0,
        gst: parseFloat(editedOrder.gst) || 0,
        discountType: editedOrder.discountType || '$',
        discountAmount: parseFloat(editedOrder.discountAmount) || 0,
        totalPrice: parseFloat(editedOrder.totalPrice) || 0,
        status: editedOrder.status || 'new'
        // Notes are added separately via handleAddNote
      };

      const response = await axios.put(
        `${config.apiUrl}/api/stationery-orders/${editedOrder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage('Order updated successfully');
        // Refresh orders list
        await fetchOrders();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving order:', error);
      setError(error.response?.data?.message || 'Failed to save order');
    }
  };

  const handleCreateProof = (orderId) => {
    navigate(`/app/proof/create/${orderId}`);
  };

  const handleCreateQuote = (orderId) => {
    // Navigate directly to quote creation without changing status
    // Status will be changed to "Quoting" when the quote is actually created
    navigate(`/app/quote/create/${orderId}`);
  };

  // Function to check if a quote exists for an order
  const [orderQuotes, setOrderQuotes] = useState({});
  
  const checkQuoteExists = async (orderId) => {
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/quote/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success && response.data.data) {
        setOrderQuotes(prev => ({ ...prev, [orderId]: response.data.data }));
        return response.data.data;
      } else {
        setOrderQuotes(prev => ({ ...prev, [orderId]: null }));
        return null;
      }
    } catch (error) {
      console.error('Error checking quote:', error);
      setOrderQuotes(prev => ({ ...prev, [orderId]: null }));
      return null;
    }
  };

  // Function to handle viewing an existing quote
  const handleViewQuote = async (orderId) => {
    const quote = orderQuotes[orderId];
    
    if (quote && quote.quoteLink) {
      window.open(quote.quoteLink, '_blank');
    } else {
      // Try to fetch quote info again in case it wasn't loaded
      const freshQuote = await checkQuoteExists(orderId);
      if (freshQuote && freshQuote.quoteLink) {
        window.open(freshQuote.quoteLink, '_blank');
      } else {
        setError('Quote not found or quote link not available');
      }
    }
  };

  // Function to handle cancelling a quote
  const handleCancelQuote = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this quote? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await axios.put(
        `${config.apiUrl}/api/quote/order/${orderId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.success) {
        setSuccessMessage('Quote cancelled successfully');
        // Refresh orders to get updated status
        await fetchOrders();
        // Clear the cached quote
        setOrderQuotes(prev => ({ ...prev, [orderId]: null }));
      } else {
        setError('Failed to cancel quote');
      }
    } catch (error) {
      console.error('Error cancelling quote:', error);
      setError('Failed to cancel quote');
    }
  };

  // Check for quotes when orders are loaded
  useEffect(() => {
    const checkQuotesForOrders = async () => {
      const ordersToCheck = stationeryOrders.filter(order => 
        order.status === 'Quoting' || order.status === 'Quoted'
      );
      
      for (const order of ordersToCheck) {
        if (!orderQuotes[order.id]) {
          await checkQuoteExists(order.id);
        }
      }
    };

    if (stationeryOrders.length > 0) {
      checkQuotesForOrders();
    }
  }, [stationeryOrders]);

  const handleStatusChange = async (orderId, newStatus, options = {}) => {
    
    // Optimistic update - update UI immediately
    const updateOrdersState = (prevOrders) => {
      return prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus }
          : order
      );
    };
    
    setStationeryOrders(updateOrdersState);
    
    // If this is a local-only update (e.g., from real-time sync), don't save to backend
    if (options.localOnly) {
      return;
    }
    
    // Save to backend
    try {
      const token = await getToken();
      const response = await axios.put(
        `${config.apiUrl}/api/orders/${orderId}/status`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.success) {
        setSuccessMessage(`Order status updated to ${newStatus}`);
      } else {
        console.error('Backend returned unsuccessful response');
        setError('Failed to update order status');
        // Revert optimistic update
        await fetchOrders();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status');
      // Revert optimistic update
      await fetchOrders();
    }
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await fetchOrders();
      } catch (error) {
      }
    }
  };

  // Handle opening the create order dialog
  const handleOpenAddDialog = () => {
    // Reset form data
    setFormData({
      organization_id: '',
      business_number: '',
      business_name: '',
      business_type: '',
      orderType: 'stationery',
      productType: '',
      notes: '',
      designNotes: '',
      quantity: '',
      size: '',
      sides: '',
      stock: '',
      finish: '',
      price: '',
      freightPrice: '',
      additionalArtworkHours: '',
      margin: '',
      artworkFee: '',
      setupFee: '',
      gst: '',
      discountType: '$',
      discountAmount: '',
      totalPrice: '',
      tempAttachments: [] // Important: initialize tempAttachments on dialog open
    });
    setOpenAddDialog(true);
  };

  // Handle closing the create order dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setEnquiryForOrder(null);
    setSelectedEnquiry(null);
    setFormData({
      organization_id: '',
      business_number: '',
      business_name: '',
      business_type: '',
      orderType: 'stationery',
      productType: '',
      notes: '',
      designNotes: '',
      quantity: '',
      size: '',
      sides: '',
      stock: '',
      finish: '',
      price: '',
      freightPrice: '',
      additionalArtworkHours: '',
      margin: '',
      artworkFee: '',
      setupFee: '',
      gst: '',
      discountType: '$',
      discountAmount: '',
      totalPrice: ''
    });
  };

  // Validate form
  const validateForm = () => {
    if (!formData.business_name) {
      setError('Business name is required');
      return false;
    }
    if (!formData.productType) {
      setError('Product type is required');
      return false;
    }
    if (!formData.quantity) {
      setError('Quantity is required');
      return false;
    }
    if (!formData.price) {
      setError('Price is required');
      return false;
    }
    return true;
  };

  // Create stationery order
  const createStationeryOrder = async (formData) => {
    try {
      // Map field names to match server expectations
      const data = { 
        orderType: 'stationery',
        organization_id: formData.organization_id || null,
        business_name: formData.business_name || '',
        business_number: formData.business_number || '',
        business_type: formData.business_type || '',
        productType: formData.productType || '',
        quantity: formData.quantity || null,
        size: formData.size || '',
        sides: formData.sides || '',
        stock: formData.stock || '',
        finish: formData.finish || '',
        price: parseFloat(formData.price) || 0,
        freightPrice: parseFloat(formData.freightPrice) || 0,
        additionalArtworkHours: parseFloat(formData.additionalArtworkHours) || 0,
        margin: parseFloat(formData.margin) || 0,
        artworkFee: parseFloat(formData.artworkFee) || 0,
        setupFee: parseFloat(formData.setupFee) || 0,
        gst: parseFloat(formData.gst) || 0,
        discountType: formData.discountType || '$',
        discountAmount: parseFloat(formData.discountAmount) || 0,
        totalPrice: parseFloat(formData.totalPrice) || 0,
        notes: formData.notes || ''
      };
      
      // Get token and CSRF token for the request
      const authToken = await getToken();
      
      // Import CSRF protection
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrfToken = await fetchCSRFToken();
      
      const response = await axios.post(
        `${config.apiUrl}/api/stationery-orders`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'X-CSRF-Token': csrfToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating stationery order:', error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    // Close dialog immediately
    setOpenAddDialog(false);
    
    try {
      const response = await createStationeryOrder(formData);
      
      // Extract order ID from response (handle different response structures)
      const orderId = response.id || response.data?.id || response.orderId;
      
      // Handle attachments if any exist
      if (formData.tempAttachments && formData.tempAttachments.length > 0 && orderId) {
        try {
          await uploadTempAttachments(orderId, formData.tempAttachments, getToken);
          
          // If this order was created from an enquiry, mark the enquiry as converted
          if (enquiryForOrder && orderId) {
            try {
              const token = await getToken();
              await axios.put(
                `${config.apiUrl}/api/stationery-enquiries/${enquiryForOrder.id}`,
                { 
                  status: 'converted',
                  convertedToOrderId: orderId
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Refresh enquiries list to remove the converted enquiry
              await fetchEnquiries();
              
              setSuccessMessage(`Stationery order created successfully with ${formData.tempAttachments.length} attachments and enquiry archived`);
            } catch (enquiryError) {
              console.error('Error updating enquiry status:', enquiryError);
              setSuccessMessage(`Stationery order created successfully with ${formData.tempAttachments.length} attachments (enquiry archive failed)`);
            }
          } else {
            setSuccessMessage(`Stationery order created successfully with ${formData.tempAttachments.length} attachments`);
          }
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
          setError(`Order created but failed to upload attachments: ${attachmentError.message}`);
        }
      } else {
        
        // If this order was created from an enquiry, mark the enquiry as converted
        if (enquiryForOrder && orderId) {
          try {
            const token = await getToken();
            await axios.put(
              `${config.apiUrl}/api/stationery-enquiries/${enquiryForOrder.id}`,
              { 
                status: 'converted',
                convertedToOrderId: orderId
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh enquiries list to remove the converted enquiry
            await fetchEnquiries();
            
            setSuccessMessage('Stationery order created successfully and enquiry archived');
          } catch (enquiryError) {
            console.error('Error updating enquiry status:', enquiryError);
            setSuccessMessage('Stationery order created successfully (enquiry archive failed)');
          }
        } else {
          setSuccessMessage('Stationery order created successfully');
        }
      }
      
      // Refresh the orders list
      await fetchOrders();
    } catch (error) {
      console.error('Error creating stationery order:', error);
      setError(`Failed to create stationery order: ${error.response?.data?.message || error.message}`);
    }
  };

  // Handle viewing enquiry details
  const handleViewEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setEnquiryDetailOpen(true);
  };

  // Handle converting enquiry to order
  const handleConvertEnquiryToOrder = async (enquiry) => {
    // Store the enquiry for reference
    setEnquiryForOrder(enquiry);
    setSelectedEnquiry(enquiry);
    
    // Fetch organization details if organizationId is present
    let organizationData = {
      organization_id: enquiry.organizationId || '',
      business_number: '',
      business_name: enquiry.schoolOrBusinessName,
      business_type: '',
      selectedOrganizationName: '' // For EnhancedSearch component
    };

    if (enquiry.organizationId) {
      try {
        const token = await getToken();
        const response = await axios.get(
          `${config.apiUrl}/api/organizations/${enquiry.organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data) {
          // Check if data is nested or direct
          const org = response.data.data || response.data;
          
          organizationData = {
            organization_id: org.id,
            business_number: org.id.toString(), // Use org ID as business number
            business_name: org.name,
            business_type: org.businessType || '',
            selectedOrganizationName: org.name // For EnhancedSearch component
          };
        }
      } catch (error) {
        console.error('Error fetching organization details:', error);
        // Continue with basic data if fetch fails
      }
    }
    
    // Pre-fill the form with enquiry and organization data
    const newFormData = {
      ...organizationData,
      orderType: 'stationery',
      productType: '',
      notes: `Converted from enquiry #${enquiry.id}\n\nCustomer Message:\n${enquiry.message}\n\nContact: ${enquiry.firstName} ${enquiry.lastName}\nEmail: ${enquiry.emailAddress}\nPhone: ${enquiry.phoneNumber}`,
      designNotes: '',
      quantity: '',
      size: '',
      sides: '',
      stock: '',
      finish: '',
      price: '',
      freightPrice: '',
      additionalArtworkHours: '',
      margin: '',
      artworkFee: '',
      setupFee: '',
      gst: '',
      discountType: '$',
      discountAmount: '',
      totalPrice: '',
      tempAttachments: [] // Important: initialize tempAttachments
    };
    
    setFormData(newFormData);
    
    // Wait a bit before opening dialog to ensure state is updated
    setTimeout(() => {
      setOpenAddDialog(true);
    }, 100);
    
    // Update enquiry status to 'contacted' or 'quoted'
    try {
      const token = await getToken();
      await axios.put(
        `${config.apiUrl}/api/stationery-enquiries/${enquiry.id}`,
        { status: 'contacted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh enquiries
      await fetchEnquiries();
    } catch (error) {
      console.error('Error updating enquiry status:', error);
    }
  };

  // Handle creating organization from enquiry
  const handleCreateOrganization = async (enquiry) => {
    setSelectedEnquiry(enquiry);
    setCreateOrgDialogOpen(true);
    // Close the enquiry detail dialog if it's open
    setEnquiryDetailOpen(false);
    // Get and store token for the dialog
    const token = await getToken();
    setAuthToken(token);
  };

  // Handle organization change for enquiry
  const handleEnquiryOrganizationChange = async (enquiryId, organizationId) => {
    try {
      const token = await getToken();
      await axios.put(
        `${config.apiUrl}/api/stationery-enquiries/${enquiryId}`,
        { organizationId: organizationId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setStationeryEnquiries(prev => 
        prev.map(enq => 
          enq.id === enquiryId 
            ? { ...enq, organizationId } 
            : enq
        )
      );
      
      // Update selected enquiry if it's the one being changed
      if (selectedEnquiry && selectedEnquiry.id === enquiryId) {
        setSelectedEnquiry({ ...selectedEnquiry, organizationId });
      }
      
      setSuccessMessage('Organisation linked to enquiry successfully');
    } catch (error) {
      console.error('Error linking organization to enquiry:', error);
      setError('Failed to link organization to enquiry');
    }
  };

  // Handle organization creation
  const handleOrganizationCreate = async (orgData) => {
    try {
      const token = await getToken();
      
      // Transform the form data to match API expectations
      const organizationPayload = {
        name: orgData.name,
        primaryName: orgData.primary_name,
        primaryEmail: orgData.primary_email,
        primaryPhone: orgData.primary_phone,
        primaryGeneralEmail: orgData.primary_general_email,
        accountsName: orgData.accounts_name,
        accountsEmail: orgData.accounts_email,
        accountsPhone: orgData.accounts_phone,
        accountsGeneralEmail: orgData.accounts_general_email,
        principalName: orgData.principal_name,
        principalEmail: orgData.principal_email,
        principalPhone: orgData.principal_phone,
        principalGeneralEmail: orgData.principal_general_email,
        businessManagerName: orgData.business_manager_name,
        businessManagerEmail: orgData.business_manager_email,
        businessManagerPhone: orgData.business_manager_phone,
        businessManagerGeneralEmail: orgData.business_manager_general_email,
        address: orgData.address,
        formattedAddress: orgData.formattedAddress,
        latitude: orgData.latitude,
        longitude: orgData.longitude,
        state: orgData.state,
        suburb: orgData.suburb,
        greaterRegion: orgData.greaterRegion,
        subRegion: orgData.subRegion,
        postcode: orgData.postcode,
        generalEmail: orgData.generalEmail,
        generalPhone: orgData.generalPhone,
        website: orgData.website,
        sameAsStreetAddress: orgData.sameAsStreetAddress,
        postalAddress: orgData.postalAddress,
        businessType: orgData.business_type,
        paymentTerms: orgData.payment_terms,
        notes: orgData.notes,
        badDebt: orgData.bad_debt,
        businessClosed: orgData.business_closed,
        isActive: orgData.is_active,
        isSchool: orgData.is_school,
        isBusiness: orgData.is_business,
        salesConsultantId: orgData.sales_consultant_id,
        numberOfFamilies: orgData.numberOfFamilies,
        schoolStatus: orgData.schoolStatus,
        schoolType: orgData.schoolType,
        paymentModel: orgData.paymentModel,
        adsEnabled: orgData.adsEnabled,
        hasSchoolLink: orgData.hasSchoolLink,
        hasEPublisher: orgData.hasEPublisher,
        hasPaper: orgData.hasPaper,
        lastInvoiceDate: orgData.lastInvoiceDate,
        agreementStartDate: orgData.agreementStartDate,
        agreementEndDate: orgData.agreementEndDate,
        schoolLinkSubscribers: orgData.schoolLinkSubscribers,
        activeSchoolLinkAds: orgData.activeSchoolLinkAds,
        deploymentsPerTerm: orgData.deploymentsPerTerm,
        ePublisherSubscribers: orgData.ePublisherSubscribers,
        ePublisherCost: orgData.ePublisherCost,
        activeEPublisherAds: orgData.activeEPublisherAds,
        paperCost: orgData.paperCost,
        paperType: orgData.paperType,
        activePaperAds: orgData.activePaperAds,
        advertisingObjectives: orgData.advertisingObjectives,
        businessStatus: orgData.businessStatus,
        additionalContacts: orgData.additionalContacts || []
      };

      const response = await axios.post(
        `${config.apiUrl}/api/organizations`,
        organizationPayload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data) {
        const newOrgId = response.data.organizationId || response.data.id;
        setSuccessMessage('Organisation created successfully');
        
        // Refresh organizations list
        await fetchOrganizations();
        
        // If this organization was created from an enquiry, link it
        if (selectedEnquiry && newOrgId) {
          await handleEnquiryOrganizationChange(selectedEnquiry.id, newOrgId);
        }
        
        setCreateOrgDialogOpen(false);
        
        // Reopen the enquiry detail dialog with the updated enquiry
        if (selectedEnquiry) {
          setEnquiryDetailOpen(true);
        }
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setError(error.response?.data?.message || 'Failed to create organization');
    }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Stationery Orders
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Manage and track stationery orders and enquiries
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            New Order
          </Button>
        </Box>

        <StationeryTabs
          stationeryOrders={stationeryOrders}
          stationeryEnquiries={stationeryEnquiries}
          loading={loading}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          filterOrders={filterOrders}
          sortOrders={sortOrders}
          getProcessedOrders={getProcessedOrders}
          getUniqueStatuses={getUniqueStatuses}
          getTotalPrice={getTotalPrice}
          handleViewDetails={handleViewDetails}
          handleStatusChange={handleStatusChange}
          ActionsMenu={ActionsMenu}
          handleViewEnquiry={handleViewEnquiry}
          handleConvertEnquiryToOrder={handleConvertEnquiryToOrder}
          handleCreateOrganization={handleCreateOrganization}
        />
      </Box>

      {/* Order Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Order Details - #{selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {editedOrder && (
            <Box sx={{ mt: 2 }}>
              {/* Business Information */}
              <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  Business Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Organisation
                    </Typography>
                    <Typography variant="body1">
                      {editedOrder.organizationName || editedOrder.businessName || editedOrder.customerName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Order Date
                    </Typography>
                    <Typography variant="body1">
                      {editedOrder.createdAt ? formatDateLong(editedOrder.createdAt) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editedOrder.status?.toLowerCase() || 'new'}
                        onChange={handleFieldChange('status')}
                        label="Status"
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="quote">Quote</MenuItem>
                        <MenuItem value="quote active">Quote Active</MenuItem>
                        <MenuItem value="quote needs changes">Quote Needs Changes</MenuItem>
                        <MenuItem value="quote declined">Quote Declined</MenuItem>
                        <MenuItem value="quote accepted">Quote Accepted</MenuItem>
                        <MenuItem value="in design">In Design</MenuItem>
                        <MenuItem value="proof">Proof</MenuItem>
                        <MenuItem value="proof active">Proof Active</MenuItem>
                        <MenuItem value="proof needs changes">Proof Needs Changes</MenuItem>
                        <MenuItem value="proof accepted">Proof Accepted</MenuItem>
                        <MenuItem value="in production">In Production</MenuItem>
                        <MenuItem value="invoiced">Invoiced</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Active Links Section */}
                  {(() => {
                    const status = editedOrder.status?.toLowerCase() || '';
                    // Statuses that come at or after quote acceptance
                    const pastQuoteAccepted = ['quote accepted', 'quote approved', 'in design', 'proof', 'proof active', 'proof needs changes', 'proof accepted', 'proof approved', 'approved', 'in production', 'invoiced', 'uploaded', 'paid'].includes(status);
                    // Statuses that come at or after proof acceptance
                    const pastProofAccepted = ['proof accepted', 'proof approved', 'approved', 'in production', 'invoiced', 'uploaded', 'paid'].includes(status);
                    const hasAnything = editedOrder.quoteLink || editedOrder.proofLink || editedOrder.invoiceLink || editedOrder.xero_invoice_url || pastQuoteAccepted || pastProofAccepted;
                    if (!hasAnything) return null;
                    return (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Active Links & Certificates
                          </Typography>

                          {/* Quote link */}
                          {editedOrder.quoteLink && (
                            <Box sx={{ mb: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(editedOrder.quoteLink, '_blank')}
                                sx={{ mr: 1 }}
                              >
                                View Quote
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                {editedOrder.quoteLink}
                              </Typography>
                            </Box>
                          )}

                          {/* Quote Certificate � show for any status at or after quote accepted */}
                          {pastQuoteAccepted && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(`${config.apiUrl}/api/quote/accepted/${editedOrder.id}`, '_blank')}
                              >
                                View Quote Certificate
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() => window.open(`${config.apiUrl}/api/quote/accepted/${editedOrder.id}?format=pdf`, '_blank')}
                              >
                                Download PDF
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                Quote accepted by client
                              </Typography>
                            </Box>
                          )}

                          {/* Proof link � only show while proof is still active (hide once accepted) */}
                          {editedOrder.proofLink && !pastProofAccepted && (
                            <Box sx={{ mb: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(editedOrder.proofLink, '_blank')}
                                sx={{ mr: 1 }}
                              >
                                View Proof
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                {editedOrder.proofLink}
                              </Typography>
                            </Box>
                          )}

                          {/* Proof Certificate � show for any status at or after proof accepted */}
                          {pastProofAccepted && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(`${config.apiUrl}/api/proof/accepted/${editedOrder.id}`, '_blank')}
                              >
                                View Proof Certificate
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() => window.open(`${config.apiUrl}/api/proof/accepted/${editedOrder.id}?format=pdf`, '_blank')}
                              >
                                Download PDF
                              </Button>
                              <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                Proof accepted by client with digital signature
                              </Typography>
                            </Box>
                          )}

                          {/* Invoice link — xero_invoice_url for Xero-generated invoices, falling
                              back to the legacy invoiceLink for orders invoiced before Xero */}
                          {getStationeryInvoiceUrl(editedOrder) && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(getStationeryInvoiceUrl(editedOrder), '_blank')}
                              >
                                View Invoice
                              </Button>
                              {editedOrder.invoiceLink && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  onClick={async () => {
                                    try {
                                      const token = await getToken();
                                      const link = document.createElement('a');
                                      link.href = `${config.apiUrl}/api/proof/download/invoice/${editedOrder.id}`;
                                      link.setAttribute('download', '');
                                      link.setAttribute('target', '_blank');
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    } catch (err) {
                                      setError('Failed to download invoice PDF');
                                    }
                                  }}
                                >
                                  Download PDF
                                </Button>
                              )}
                              {canCancelStationeryInvoice(editedOrder) && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    if (!window.confirm('Are you sure you want to cancel this invoice? This will void it in Xero and set the status back to In Production.')) return;
                                    try {
                                      const token = await getToken();
                                      const response = await axios.post(
                                        getStationeryInvoiceCancelUrl(editedOrder, config.apiUrl),
                                        { reason: 'Invoice cancelled by admin' },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      if (response.data?.success) {
                                        setSuccessMessage(response.data.message || 'Invoice cancelled successfully.');
                                        await fetchOrders();
                                      } else {
                                        setError(response.data?.message || 'Failed to cancel invoice');
                                      }
                                    } catch (err) {
                                      setError('Failed to cancel invoice: ' + (err.response?.data?.message || err.message));
                                    }
                                  }}
                                >
                                  Cancel Invoice
                                </Button>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    );
                  })()}
                </Grid>
              </Paper>

              {/* Product Information */}
              <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  Product Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Product Type"
                      value={editedOrder.productType || ''}
                      onChange={handleFieldChange('productType')}
                      placeholder="e.g., Business Cards, Flyers"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Quantity"
                      value={editedOrder.quantity || ''}
                      onChange={handleFieldChange('quantity')}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Size"
                      value={editedOrder.size || ''}
                      onChange={handleFieldChange('size')}
                      placeholder="e.g., A4, DL"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sides</InputLabel>
                      <Select
                        value={editedOrder.sides || ''}
                        onChange={handleFieldChange('sides')}
                        label="Sides"
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="1">Single Sided</MenuItem>
                        <MenuItem value="2">Double Sided</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Stock"
                      value={editedOrder.stock || ''}
                      onChange={handleFieldChange('stock')}
                      placeholder="e.g., 300gsm"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Finish"
                      value={editedOrder.finish || ''}
                      onChange={handleFieldChange('finish')}
                      placeholder="e.g., Matt, Gloss"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Pricing Information */}
              <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  Pricing Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Price"
                      value={editedOrder.price || ''}
                      onChange={handleFieldChange('price')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Freight Price"
                      value={editedOrder.freightPrice || ''}
                      onChange={handleFieldChange('freightPrice')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Additional Artwork Hours"
                      value={editedOrder.additionalArtworkHours || ''}
                      onChange={handleFieldChange('additionalArtworkHours')}
                      inputProps={{ min: 0, step: '0.5' }}
                      helperText={`Charged at $${stationerySettings.artworkFee}/hour`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Artwork Fee"
                      value={`$${editedOrder.artworkFee || '0.00'}`}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                      helperText="Auto-calculated"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Margin"
                      value={`$${editedOrder.margin || '0.00'}`}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                      helperText={`${stationerySettings.marginPercentage}% of price`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Setup Fee"
                      value={`$${editedOrder.setupFee || '0.00'}`}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                      helperText="Auto-calculated"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="GST"
                      value={`$${editedOrder.gst || '0.00'}`}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                      helperText={`${stationerySettings.gstPercentage}% of subtotal`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        value={editedOrder.discountType || '$'}
                        onChange={handleFieldChange('discountType')}
                        label="Discount Type"
                      >
                        <MenuItem value="$">$</MenuItem>
                        <MenuItem value="%">%</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Discount Amount"
                      value={editedOrder.discountAmount || ''}
                      onChange={handleFieldChange('discountAmount')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">
                          {editedOrder.discountType === '%' ? '%' : '$'}
                        </InputAdornment>,
                        inputProps: { min: 0, step: editedOrder.discountType === '%' ? '1' : '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Total Price"
                      value={`$${editedOrder.totalPrice || '0.00'}`}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                      sx={{
                        '& .MuiFilledInput-root': {
                          backgroundColor: 'primary.light',
                          fontSize: '1.1rem',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Notes */}
              <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Add new note */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Add Note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Enter a note for this stationery order..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleAddNote();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      size="small"
                    >
                      Add Note
                    </Button>
                  </Box>
                </Box>

                {/* Display existing notes */}
                {(() => {
                  const parsedNotes = parseNotesIntoArray(editedOrder.notes);
                  
                  if (parsedNotes.length === 0) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No notes added yet. Add a note above to get started.
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        Order Notes ({parsedNotes.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {parsedNotes.map((note) => {
                          const color = note.author === 'Customer' ? 'info' : note.author === 'System' ? 'action' : 'primary';
                          const bgColor = note.author === 'Customer' ? 'info.50' : note.author === 'System' ? 'grey.100' : 'grey.50';
                          const borderColor = note.author === 'Customer' ? 'info.200' : note.author === 'System' ? 'grey.300' : 'grey.200';
                          return (
                            <NoteCard
                              key={note.id}
                              note={{ content: note.content, createdBy: note.author, createdAt: note.timestamp, sourceLabel: note.sourceLabel }}
                              color={color}
                              bgColor={bgColor}
                              borderColor={borderColor}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Paper>

              {/* Attachments */}
              <Paper sx={{ p: 2, mb: 2 }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Attachments
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {loadingAttachments ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading attachments...
                    </Typography>
                  </Box>
                ) : orderAttachments.length > 0 ? (
                  <Box>
                    {orderAttachments.map((attachment) => (
                      <Paper
                        key={attachment.id}
                        sx={{
                          p: 2,
                          mb: 1,
                          bgcolor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {attachment.originalName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(attachment.fileSize / 1024).toFixed(2)} KB � 
                              Uploaded by {attachment.uploaderName || 'Unknown'} � 
                              {new Date(attachment.uploadedAt).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.originalName)}
                            title="Download"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No attachments for this order.
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Workflow History */}
              {selectedOrder?.id && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Workflow History
                  </Typography>
                  <WorkflowHistory orderId={selectedOrder.id} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveOrderDetails} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <OrderWorkflowNotesDialog
        open={notesDialog.open}
        onClose={handleCloseNotesDialog}
        onSave={handleSaveNotes}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
      />

      {/* Enquiry Detail Dialog */}
      <EnquiryDetailDialog
        open={enquiryDetailOpen}
        onClose={() => {
          setEnquiryDetailOpen(false);
          setSelectedEnquiry(null);
        }}
        enquiry={selectedEnquiry}
        onConvertToOrder={handleConvertEnquiryToOrder}
        organizations={organizations}
        onCreateOrganization={handleCreateOrganization}
        onOrganizationChange={handleEnquiryOrganizationChange}
      />

      {/* Create Order Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={handleCloseAddDialog} 
        maxWidth={enquiryForOrder && selectedEnquiry ? "xl" : "md"}
        fullWidth
      >
        <DialogTitle>
          New Stationery Order {enquiryForOrder && `(from Enquiry #${enquiryForOrder.id})`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Enquiry Reference Panel */}
            {enquiryForOrder && selectedEnquiry && (
              <Grid item xs={12} md={4}>
                <EnquiryReferencePanel 
                  enquiry={enquiryForOrder}
                  onClose={() => setSelectedEnquiry(null)}
                />
              </Grid>
            )}
            
            {/* Order Form */}
            <Grid item xs={12} md={enquiryForOrder && selectedEnquiry ? 8 : 12}>
              {enquiryForOrder && !selectedEnquiry && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedEnquiry(enquiryForOrder)}
                    startIcon={<Visibility />}
                  >
                    Show Enquiry Reference
                  </Button>
                </Box>
              )}
              <StationeryOrderForm 
                formData={formData} 
                setFormData={setFormData} 
                organizations={organizations} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            disabled={
              !formData.business_name || !formData.productType || 
              !formData.quantity || !formData.price
            }
          >
            Create Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Organisation Dialog */}
      <OrganizationDialog
        open={createOrgDialogOpen}
        onClose={() => {
          setCreateOrgDialogOpen(false);
          setSelectedEnquiry(null);
        }}
        onSubmit={handleOrganizationCreate}
        isEdit={false}
        organization={selectedEnquiry ? {
          name: selectedEnquiry.schoolOrBusinessName,
          primary_name: `${selectedEnquiry.firstName} ${selectedEnquiry.lastName}`,
          primary_email: selectedEnquiry.emailAddress,
          primary_phone: selectedEnquiry.phoneNumber,
          is_business: true,
          is_active: true
        } : null}
        token={authToken}
      />
    </Container>
  );
};

export default StationeryOrders;

